import { useCallback, useSyncExternalStore } from 'react';

/** DESIGN.md §12: data-theme on <html>, saved in localStorage('theme'), OS preference first time. */
export type Theme = 'light' | 'dark';

const STORAGE_KEY = 'theme';
const META_COLORS: Record<Theme, string> = { light: '#f7f8fc', dark: '#080a0f' };
const listeners = new Set<() => void>();

function readSaved(): Theme | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === 'light' || v === 'dark' ? v : null;
  } catch {
    return null;
  }
}

function systemTheme(): Theme {
  return typeof matchMedia === 'function' && matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function getTheme(): Theme {
  const current = document.documentElement.dataset.theme;
  return current === 'dark' || current === 'light' ? current : (readSaved() ?? systemTheme());
}

let transitionTimer: number | undefined;

export function applyTheme(theme: Theme, options: { persist?: boolean; animate?: boolean } = {}): void {
  const root = document.documentElement;
  if (options.animate !== false) {
    root.classList.add('theme-transition');
    window.clearTimeout(transitionTimer);
    transitionTimer = window.setTimeout(() => root.classList.remove('theme-transition'), 260);
  }
  root.dataset.theme = theme;
  for (const meta of document.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]')) {
    meta.removeAttribute('media');
    meta.content = META_COLORS[theme];
  }
  if (options.persist !== false) {
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      // storage unavailable; theme still applies for this page
    }
  }
  for (const l of listeners) l();
}

export function toggleTheme(): Theme {
  const next: Theme = getTheme() === 'dark' ? 'light' : 'dark';
  applyTheme(next);
  return next;
}

let systemWatchInstalled = false;
function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  if (!systemWatchInstalled && typeof window !== 'undefined') {
    systemWatchInstalled = true;
    // Follow the OS only until the user picks a theme.
    matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
      if (!readSaved()) applyTheme(systemTheme(), { persist: false });
    });
    // Keep tabs in sync.
    window.addEventListener('storage', (e) => {
      if (e.key === STORAGE_KEY && (e.newValue === 'light' || e.newValue === 'dark')) applyTheme(e.newValue, { persist: false });
    });
  }
  return () => listeners.delete(listener);
}

export function useTheme(): { theme: Theme; setTheme: (t: Theme) => void; toggle: () => void } {
  const theme = useSyncExternalStore(subscribe, getTheme, () => 'light' as Theme);
  const setTheme = useCallback((t: Theme) => applyTheme(t), []);
  const toggle = useCallback(() => void toggleTheme(), []);
  return { theme, setTheme, toggle };
}
